import { searchWatches } from './search'
import type { CatalogFilters, Collection, SortKey, Watch } from '@/types'

/**
 * Catalogue rules — filtering, sorting and the public/private split.
 *
 * All of it is pure functions over an array so the same logic serves the public
 * collection page and the owner's listing table without a second code path.
 */

/** Rows the public is allowed to see. Drafts belong to the owner alone. */
export function publicWatches(rows: Watch[]): Watch[] {
  return rows.filter((w) => w.status === 'published' || w.status === 'reserved')
}

export const PRICE_RANGES: { value: string; labelKey: string; min: number; max: number }[] = [
  { value: '', labelKey: 'catalog.priceAny', min: 0, max: Infinity },
  { value: 'under5k', labelKey: 'catalog.priceUnder5k', min: 0, max: 5000 },
  { value: '5to10k', labelKey: 'catalog.price5to10k', min: 5000, max: 10000 },
  { value: '10to20k', labelKey: 'catalog.price10to20k', min: 10000, max: 20000 },
  { value: 'over20k', labelKey: 'catalog.priceOver20k', min: 20000, max: Infinity },
]

export const SORT_OPTIONS: { value: SortKey; labelKey: string }[] = [
  { value: 'relevance', labelKey: 'catalog.sortRelevance' },
  { value: 'newest', labelKey: 'catalog.sortNewest' },
  { value: 'featured', labelKey: 'catalog.sortFeatured' },
  { value: 'price_asc', labelKey: 'catalog.sortPriceAsc' },
  { value: 'price_desc', labelKey: 'catalog.sortPriceDesc' },
]

export const EMPTY_FILTERS: CatalogFilters = {
  q: '',
  collection: '',
  maison: '',
  movement: '',
  priceRange: '',
  sort: 'relevance',
}

function timeOf(row: Watch): number {
  const t = new Date(row.created_at ?? 0).getTime()
  return Number.isFinite(t) ? t : 0
}

export function sortWatches(rows: Watch[], sort: SortKey): Watch[] {
  const out = rows.slice()
  switch (sort) {
    // 'relevance' is decided by the search engine, which has already ordered
    // the rows; with no query there is nothing to be relevant to, so the list
    // falls through to newest-first.
    case 'price_asc':
      return out.sort((a, b) => a.price - b.price)
    case 'price_desc':
      return out.sort((a, b) => b.price - a.price)
    case 'featured':
      return out.sort((a, b) => Number(b.is_featured) - Number(a.is_featured) || timeOf(b) - timeOf(a))
    case 'relevance':
    case 'newest':
    default:
      return out.sort((a, b) => timeOf(b) - timeOf(a))
  }
}

/**
 * Apply the whole filter set.
 *
 * Order matters: the drop-downs narrow the list FIRST, then the search engine
 * ranks what is left. Ranking a set the shopper has already excluded would let
 * a high-scoring but filtered-out piece reorder the page around it.
 *
 * With a query and 'relevance' sorting, the engine's order is kept as-is —
 * that is the whole point of relevance. Any other sort overrules it.
 */
export function filterWatches(
  rows: Watch[],
  filters: CatalogFilters,
  collections: Collection[] = [],
): Watch[] {
  const range = PRICE_RANGES.find((r) => r.value === filters.priceRange)

  const narrowed = rows.filter((w) => {
    if (filters.collection && w.collection !== filters.collection) return false
    if (filters.maison && w.maison !== filters.maison) return false
    if (filters.movement && w.movement !== filters.movement) return false
    if (range && (w.price < range.min || w.price > range.max)) return false
    return true
  })

  const query = filters.q.trim()
  if (!query) return sortWatches(narrowed, filters.sort)

  const ranked = searchWatches(narrowed, query, { collections }).map((h) => h.watch)
  return filters.sort === 'relevance' ? ranked : sortWatches(ranked, filters.sort)
}

/** Distinct maison names, alphabetical — feeds the "Maison" filter. */
export function maisonsOf(rows: Watch[]): string[] {
  return [...new Set(rows.map((w) => w.maison).filter(Boolean))].sort((a, b) => a.localeCompare(b))
}

/** Same collection first, then anything else; never the piece itself. */
export function relatedWatches(rows: Watch[], current: Watch, limit = 3): Watch[] {
  const others = publicWatches(rows).filter((w) => w.id !== current.id)
  const sameFamily = others.filter((w) => w.collection === current.collection)
  const rest = others.filter((w) => w.collection !== current.collection)
  return [...sameFamily, ...rest].slice(0, limit)
}

/** How many filters the visitor has actually set — drives the "clear" button. */
export function activeFilterCount(filters: CatalogFilters): number {
  return [filters.q, filters.collection, filters.maison, filters.movement, filters.priceRange].filter(
    Boolean,
  ).length
}
